/**
 * The one load step for a material: a scene material as it is, or a `.tres` through the
 * cached `.tres` parse, into the same shape. A slot renders what this returns and never
 * learns which file the material came from.
 */
import { useEffect, useMemo } from 'react';
import { warn } from '../../logger';
import { fileResources, findSectionResource } from '../../resources/resourceSection';
import { resourceFilePath } from '../../resources/subResourcePath';
import { useResourceLoad } from '../../resources/useResource';
import { useMissingReport } from '../contexts/MissingResourcesContext';
import { held } from './materialSource';
import { NODE_PROPERTY } from '../../resources/useTexture2D';
const ABSENT = { status: 'absent' };
/** The material to draw, or null for Godot's default surface. */
export function readyMaterial(loaded) {
    return loaded.status === 'ready' ? loaded.material : null;
}
/** What `source` loads to. A ShaderMaterial is declined with a warning (ADR-0041). */
export function useMaterial(source) {
    const address = source?.kind === 'file' ? source.path : '';
    // Called with '' for an inline source, to keep the hook count stable. The `resource` bus
    // parses whole files, so the hook loads the file and reports under the address itself.
    const fileResult = useResourceLoad(resourceFilePath(address), 'resource');
    const file = fileResult.value;
    const body = useMemo(() => (file ? findSectionResource(file, address) : undefined), [file, address]);
    useMissingReport(address, addressStatus(fileResult.status, body));
    // Keyed on what the source holds, not the source object: a caller may build a new
    // source each render, and a new material would rebind every texture.
    const inline = source?.kind === 'inline' ? source.material : null;
    const inlineResource = inline?.resource;
    const inlineInternal = inline?.internalResources;
    const inlineExternal = inline?.externalResources;
    const isHeldLocally = source?.isHeldLocally ?? NODE_PROPERTY;
    const loaded = useMemo(() => {
        const holding = held(isHeldLocally);
        if (inlineResource && inlineInternal && inlineExternal) {
            return ready({
                resource: inlineResource,
                internalResources: inlineInternal,
                externalResources: inlineExternal,
                ...holding,
            });
        }
        if (!file || !body)
            return ABSENT;
        return ready({
            resource: body,
            ...fileResources(file),
            // The file's own address, so a chain's visited key holds the file an inline id
            // resolves in: two files' ids of one name stay two keys.
            origin: resourceFilePath(address),
            ...holding,
        });
    }, [inlineResource, inlineInternal, inlineExternal, isHeldLocally, file, body, address]);
    useEffect(() => {
        if (loaded.status === 'declined' && loaded.type === 'ShaderMaterial') {
            warn("[material] ShaderMaterial is not compiled — rendering Godot's default 3D surface.");
        }
    }, [loaded]);
    return loaded;
}
/** A loaded file without the named `[sub_resource]` fails the address, as Godot's load does. */
function addressStatus(fileStatus, body) {
    if (fileStatus !== 'loaded')
        return fileStatus;
    return body ? 'loaded' : 'unavailable';
}
/** Ready for a StandardMaterial3D, declined for any other type. */
function ready(material) {
    const { type } = material.resource;
    return type === 'StandardMaterial3D' ? { status: 'ready', material } : { status: 'declined', type };
}
//# sourceMappingURL=useMaterial.js.map